import React, { useState, useEffect, useRef } from 'react';
import { useInViewport } from '../../hooks/useInViewport';

/**
 * Mounts `children` only once the placeholder gets close to the viewport.
 *
 * The space of the section (`minHeight`) is reserved from the very beginning, and it stays
 * reserved after the reveal until the section is "settled": its content is at least
 * `settleHeight` tall, or `settleTimeout` ms have passed (sections that end up empty). Sections
 * fetch their data on mount and render a spinner or nothing meanwhile, so without the reserved
 * space a revealed section would collapse, pull the placeholders below it into the viewport and
 * get all of them revealed at once.
 *
 * While `computing` is `true` (the data the section depends on is not resolved yet) the placeholder
 * is kept and nothing is observed.
 *
 * Do not use it for the above-the-fold content.
 */
const LazySection = ({
    children,
    computing = false,
    minHeight = 400,
    rootMargin = '300px 0px',
    settleHeight = 200,
    settleTimeout = 3000,
}) => {
    const [ref, inViewport] = useInViewport({ rootMargin, enabled: !computing });
    const [settled, setSettled] = useState(false);
    const contentRef = useRef(null);

    const revealed = inViewport && !computing;

    useEffect(() => {
        if (!revealed || settled) {
            return;
        }

        const timeoutId = setTimeout(() => setSettled(true), settleTimeout);
        let observer;

        if (contentRef.current && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(([entry]) => {
                if (entry.contentRect.height >= settleHeight) {
                    setSettled(true);
                }
            });
            observer.observe(contentRef.current);
        }

        return () => {
            clearTimeout(timeoutId);

            if (observer) {
                observer.disconnect();
            }
        };
    }, [revealed, settled, settleHeight, settleTimeout]);

    if (!revealed) {
        return <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
    }

    return (
        <div style={{ minHeight: settled ? undefined : minHeight }}>
            <div ref={contentRef}>{children}</div>
        </div>
    );
};

export default LazySection;
